import { OperationOutcome, Organization } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UsePartnerResult {
  organization: Organization | undefined;
  organizationLoading: boolean;
  organizationError: OperationOutcome | Error | undefined;
  refreshOrganization: () => Promise<void>;
  updateOrganization: (updatedOrganization: Organization) => Promise<void>;
}

export function usePartner(organizationId: string | undefined): UsePartnerResult {
  const medplum = useMedplum();
  const [organization, setOrganization] = useState<Organization | undefined>(undefined);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationError, setOrganizationError] = useState<OperationOutcome | Error | undefined>(undefined);

  const loadOrganization = useCallback(async () => {
    if (!organizationId) return;

    setOrganizationLoading(true);
    setOrganizationError(undefined);
    try {
      const organization = await medplum.readResource('Organization', organizationId);
      setOrganization(organization);
    } catch (e) {
      setOrganization(undefined);
      setOrganizationError(e as OperationOutcome | Error);
    } finally {
      setOrganizationLoading(false);
    }
  }, [organizationId, medplum]);

  const updateOrganization = useCallback(
    async (updatedOrganization: Organization) => {
      if (!updatedOrganization) return;
      setOrganization(updatedOrganization);
      const updated = await medplum.updateResource(updatedOrganization);
      setOrganization(updated);
    },
    [medplum]
  );

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  return {
    organization,
    organizationLoading,
    organizationError,
    refreshOrganization: loadOrganization,
    updateOrganization,
  };
}
